import { useEffect, useRef, useState } from "react";
import {
  MenuButtonOrderedList,
  RichTextEditor as MuiRichTextEditor,
  type RichTextEditorRef,
} from "mui-tiptap";
import StarterKit from "@tiptap/starter-kit";
import {
  MenuButtonBold,
  MenuButtonItalic,
  MenuControlsContainer,
  MenuDivider,
  MenuButtonBulletedList,
} from "mui-tiptap";
import CharacterCount from "@tiptap/extension-character-count";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { LinearProgress, Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { MenuButtonLink } from "../MenuButtonLink";
import { MenuButtonEmoji } from "../MenuButtonEmoji";
import { MenuButtonUnderline } from "../MenuButtonUnderline";
import type { Editor } from "@tiptap/core";

interface RichTextEditorProps {
  value: string | undefined;
  onChange: (content: string) => void;
}

const LIMIT = 2000;

export function RichTextEditor({ value, onChange }: RichTextEditorProps) {
  const rteRef = useRef<RichTextEditorRef>(null);
  const [editorInstance, setEditorInstance] = useState<Editor | null>(null);
  const editor = editorInstance;
  const count = editor?.storage.characterCount?.characters() ?? 0;
  const percentage = Math.round((100 / LIMIT) * count);

  useEffect(() => {
    if (!editor) return;

    const handleUpdate = ({ editor }: { editor: Editor }) => {
      onChange(editor.getHTML());
    };
    editor.on("update", handleUpdate);

    return () => {
      editor.off("update", handleUpdate);
    };
  }, [editor, onChange]);

  return (
    <div>
      <MuiRichTextEditor
        ref={rteRef}
        extensions={[
          StarterKit,
          CharacterCount.configure({ limit: LIMIT }),
          Link.configure({ openOnClick: true }),
          Underline,
        ]}
        content={value}
        onCreate={({ editor }) => setEditorInstance(editor)}
        renderControls={() =>
          editor ? (
            <MenuControlsContainer>
              <MenuButtonBold />
              <MenuButtonItalic />
              <MenuButtonUnderline editor={editor} iconSize="small" />
              <MenuDivider />
              <MenuButtonBulletedList />
              <MenuButtonOrderedList />
              <MenuDivider />
              <MenuButtonLink editor={editor} />
              <MenuButtonEmoji editor={editor} iconSize="small" />
            </MenuControlsContainer>
          ) : null
        }
        editorProps={{
          attributes: {
            style: "min-height: 72px;", // Approx. 3 lines (3 * 24px line-height)
          },
        }}
      />
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box sx={{ width: "100%", mr: 1 }}>
          <LinearProgress
            variant="determinate"
            color={count >= LIMIT ? "error" : "primary"}
            value={percentage}
          />
        </Box>
        <Box sx={{ minWidth: 100 }}>
          <Typography variant="caption" color={count >= LIMIT ? "error" : "textSecondary"}>
            {count} / {LIMIT}
          </Typography>
        </Box>
      </Box>
    </div>
  );
}
